import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Alert,
  AlertTitle,
  Link,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getDataQualityMetrics } from '../../../api/utilityServices';

interface DataQualityOverviewProps {
  isLoading: boolean;
}

const DataQualityOverview: React.FC<DataQualityOverviewProps> = ({ isLoading }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await getDataQualityMetrics();
        setData(result);
      } catch (err) {
        setError('Failed to fetch data quality metrics');
        console.error('Error fetching data quality metrics:', err);
      } finally {
        setLoading(false);
      }
    };

    if (!isLoading) {
      fetchData();
    }
  }, [isLoading]);

  const handleClientClick = (clientId: string) => {
    navigate(`/utilities/clients/${clientId}`);
  };

  const getSeverityColor = (count: number, total: number) => {
    const percentage = (count / total) * 100;
    if (percentage === 0) return 'success';
    if (percentage < 10) return 'warning';
    return 'error';
  };

  const getSeverityIcon = (count: number, total: number) => {
    const percentage = (count / total) * 100;
    if (percentage === 0) return <CheckCircleIcon color="success" />;
    if (percentage < 10) return <WarningIcon color="warning" />;
    return <ErrorIcon color="error" />;
  };

  if (loading || isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Data Quality Overview
          </Typography>
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>Loading data quality metrics...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Data Quality Overview
          </Typography>
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            {error || 'No data available'}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const totalIssues = 
    data.clientDataCompleteness.summary.missingContactInfo +
    data.clientDataCompleteness.summary.incompleteBusinessDetails +
    data.clientDataCompleteness.summary.contactPersonGaps +
    data.contractDataIntegrity.summary.missingContractDates +
    data.contractDataIntegrity.summary.incompleteContractValues +
    data.contractDataIntegrity.summary.supplierInfoGaps +
    data.serviceConfigurationIssues.summary.orphanedServices +
    data.serviceConfigurationIssues.summary.missingServiceDetails +
    data.serviceConfigurationIssues.summary.potentialDuplicates +
    data.commissionTrackingQuality.summary.missingCommissionData +
    data.commissionTrackingQuality.summary.inconsistentCommissionStructures +
    data.commissionTrackingQuality.summary.incompleteUpliftInfo +
    data.dataFreshnessIndicators.summary.staleClients +
    data.dataFreshnessIndicators.summary.outdatedContracts;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ color: '#fff', fontWeight: 'bold' }}>
          Data Quality Overview
        </Typography>
        
        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ color: totalIssues === 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                  {totalIssues}
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff' }}>
                  Total Issues
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
                  {data.clientDataCompleteness.totalClients}
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff' }}>
                  Total Clients
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
                  {data.contractDataIntegrity.totalServices}
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff' }}>
                  Total Services
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ color: totalIssues === 0 ? 'success.main' : 'warning.main', fontWeight: 'bold' }}>
                  {totalIssues === 0 ? '100%' : `${Math.round(((data.clientDataCompleteness.totalClients + data.contractDataIntegrity.totalServices - totalIssues) / (data.clientDataCompleteness.totalClients + data.contractDataIntegrity.totalServices)) * 100)}%`}
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff' }}>
                  Data Quality
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Detailed Issues */}
        <Box sx={{ mt: 3 }}>
          {/* Client Data Completeness */}
          <Accordion sx={{ backgroundColor: 'rgba(255,255,255,0.05)', mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                {getSeverityIcon(
                  data.clientDataCompleteness.summary.missingContactInfo +
                  data.clientDataCompleteness.summary.incompleteBusinessDetails +
                  data.clientDataCompleteness.summary.contactPersonGaps,
                  data.clientDataCompleteness.totalClients
                )}
                <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>
                  Client Data Completeness
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                  <Chip 
                    label={`${data.clientDataCompleteness.summary.missingContactInfo + data.clientDataCompleteness.summary.incompleteBusinessDetails + data.clientDataCompleteness.summary.contactPersonGaps} issues`}
                    color={getSeverityColor(
                      data.clientDataCompleteness.summary.missingContactInfo +
                      data.clientDataCompleteness.summary.incompleteBusinessDetails +
                      data.clientDataCompleteness.summary.contactPersonGaps,
                      data.clientDataCompleteness.totalClients
                    )}
                    size="small"
                  />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
                    Missing Contact Info: {data.clientDataCompleteness.summary.missingContactInfo}
                  </Typography>
                  {data.clientDataCompleteness.issues.missingContactInfo.slice(0, 3).map((issue: any, index: number) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleClientClick(issue.clientId)}
                        sx={{ 
                          color: '#fff',
                          textDecoration: 'underline',
                          '&:hover': { textDecoration: 'none' }
                        }}
                      >
                        {issue.companyName}
                      </Link>
                    </Box>
                  ))}
                  {data.clientDataCompleteness.issues.missingContactInfo.length > 3 && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      +{data.clientDataCompleteness.issues.missingContactInfo.length - 3} more
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
                    Incomplete Business Details: {data.clientDataCompleteness.summary.incompleteBusinessDetails}
                  </Typography>
                  {data.clientDataCompleteness.issues.incompleteBusinessDetails.slice(0, 3).map((issue: any, index: number) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleClientClick(issue.clientId)}
                        sx={{ 
                          color: '#fff',
                          textDecoration: 'underline',
                          '&:hover': { textDecoration: 'none' }
                        }}
                      >
                        {issue.companyName}
                      </Link>
                    </Box>
                  ))}
                  {data.clientDataCompleteness.issues.incompleteBusinessDetails.length > 3 && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      +{data.clientDataCompleteness.issues.incompleteBusinessDetails.length - 3} more
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
                    Contact Person Gaps: {data.clientDataCompleteness.summary.contactPersonGaps}
                  </Typography>
                  {data.clientDataCompleteness.issues.contactPersonGaps.slice(0, 3).map((issue: any, index: number) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleClientClick(issue.clientId)}
                        sx={{ 
                          color: '#fff',
                          textDecoration: 'underline',
                          '&:hover': { textDecoration: 'none' }
                        }}
                      >
                        {issue.companyName}
                      </Link>
                    </Box>
                  ))}
                  {data.clientDataCompleteness.issues.contactPersonGaps.length > 3 && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      +{data.clientDataCompleteness.issues.contactPersonGaps.length - 3} more
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Contract Data Integrity */}
          <Accordion sx={{ backgroundColor: 'rgba(255,255,255,0.05)', mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                {getSeverityIcon(
                  data.contractDataIntegrity.summary.missingContractDates +
                  data.contractDataIntegrity.summary.incompleteContractValues +
                  data.contractDataIntegrity.summary.supplierInfoGaps,
                  data.contractDataIntegrity.totalServices
                )}
                <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>
                  Contract Data Integrity
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                  <Chip 
                    label={`${data.contractDataIntegrity.summary.missingContractDates + data.contractDataIntegrity.summary.incompleteContractValues + data.contractDataIntegrity.summary.supplierInfoGaps} issues`}
                    color={getSeverityColor(
                      data.contractDataIntegrity.summary.missingContractDates +
                      data.contractDataIntegrity.summary.incompleteContractValues +
                      data.contractDataIntegrity.summary.supplierInfoGaps,
                      data.contractDataIntegrity.totalServices
                    )}
                    size="small"
                  />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
                    Missing Contract Dates: {data.contractDataIntegrity.summary.missingContractDates}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
                    Incomplete Contract Values: {data.contractDataIntegrity.summary.incompleteContractValues}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
                    Supplier Info Gaps: {data.contractDataIntegrity.summary.supplierInfoGaps}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Data Freshness */}
          <Accordion sx={{ backgroundColor: 'rgba(255,255,255,0.05)', mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <InfoIcon color="info" />
                <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>
                  Data Freshness Indicators
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                  <Chip 
                    label={`${data.dataFreshnessIndicators.summary.staleClients + data.dataFreshnessIndicators.summary.outdatedContracts} stale records`}
                    color="info"
                    size="small"
                  />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
                    Stale Clients (3+ months): {data.dataFreshnessIndicators.summary.staleClients}
                  </Typography>
                  {data.dataFreshnessIndicators.staleClients.slice(0, 5).map((client: any, index: number) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleClientClick(client.clientId)}
                        sx={{ 
                          color: '#fff',
                          textDecoration: 'underline',
                          '&:hover': { textDecoration: 'none' }
                        }}
                      >
                        {client.companyName}
                      </Link>
                      <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                        ({client.daysSinceUpdate} days ago)
                      </Typography>
                    </Box>
                  ))}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
                    Outdated Contracts (6+ months): {data.dataFreshnessIndicators.summary.outdatedContracts}
                  </Typography>
                  {data.dataFreshnessIndicators.outdatedContracts.slice(0, 5).map((contract: any, index: number) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#fff' }}>
                        {contract.clientName} - {contract.serviceType}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        ({contract.daysSinceUpdate} days ago)
                      </Typography>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DataQualityOverview;
